// src/pages/projects/ProjectQuotationPage.jsx
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, Plus, Link as LinkIcon, Eye, Trash2, Mail, Copy, Check, FileText, HardDrive, Database, AlertCircle, ArrowRightLeft, Coins, Edit, X } from 'lucide-react';
import { generateId, formatCurrency, formatBytes } from '@/lib/app-utils';
import { saveFileToIDB, getFileFromIDB, deleteFileFromIDB, getStorageEstimate, cleanupOldFiles, getFileCount } from '@/lib/idb-storage';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import FileUpload from '@/components/ui/FileUpload';
import { useNotifications } from '@/contexts/NotificationContext';
import { useFileViewer } from '@/contexts/FileViewerContext';
import { projectsAPI } from '@/services/projects';
import { quotationsAPI } from '@/services/quotations';

const ProjectQuotationPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addNotification } = useNotifications();
  const { openViewer } = useFileViewer();

  const [project, setProject] = useState(null);
  const [quotations, setQuotations] = useState([]);

  // Add Form State
  const [quotationNumber, setQuotationNumber] = useState('');
  const [description, setDescription] = useState('');
  const [unitType, setUnitType] = useState('L/S');
  const [price, setPrice] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [scrapValue, setScrapValue] = useState('');

  // Storage & Files
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [storageStats, setStorageStats] = useState({ usage: 0, quota: 0, fileCount: 0 });
  const [isCleaning, setIsCleaning] = useState(false);

  // Share Dialog State
  const [shareOpen, setShareOpen] = useState(false);
  const [currentShareLink, setCurrentShareLink] = useState('');
  const [copied, setCopied] = useState(false);

  // Edit Dialog State
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingQuotation, setEditingQuotation] = useState(null);
  const [editForm, setEditForm] = useState({
    quotationNumber: '',
    version: '',
    description: '',
    unitType: 'L/S',
    quantity: 1,
    price: '',
    scrapValue: '',
    keepFile: false
  });
  const [editFile, setEditFile] = useState(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  useEffect(() => {
    fetchData();
    refreshStorageStats();
  }, [id, navigate]);

  const fetchData = async () => {
  try {
    const projectRes = await projectsAPI.details(id);
    
    if (projectRes.success) {
      setProject({
        ...projectRes.data,
        projectName: projectRes.data.project_name || projectRes.data.projectName
      });
    } else {
      navigate('/projects');
      return;
    }

    const quotationsRes = await quotationsAPI.list(id);
    
    if (quotationsRes.success) {
      const normalized = quotationsRes.data.map(q => ({
        id: q.id,
        projectId: q.project_id || q.projectId,
        quotationNumber: q.quotation_number || q.quotationNumber,
        description: q.description,
        unitType: q.unit_type || q.unitType,
        price: parseFloat(q.price),
        quantity: parseFloat(q.quantity),
        scrapValue: parseFloat(q.scrap_value || q.scrapValue || 0),
        total: parseFloat(q.total),
        netValue: parseFloat(q.net_value || q.netValue),
        createdAt: q.created_at || q.createdAt,
        version: q.version,
        versionType: q.version_type || q.versionType,
        fileId: q.file_id || q.fileId,
        pdfFileName: q.pdf_file_name || q.pdfFileName,
        fileSize: q.file_size || q.fileSize,
        fileUrl: q.file_url || q.fileUrl // ✅ NEW
      }));

      const sortedQuotations = normalized.sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      );
      
      setQuotations(sortedQuotations);
    }
  } catch (error) {
    console.error('Failed to load data:', error);
    addNotification('Error', 'Failed to load quotations', 'error');
  }
};


  const refreshStorageStats = async () => {
    const stats = await getStorageEstimate();
    const count = await getFileCount();
    setStorageStats({ ...stats, fileCount: count });
  };

  const handleCleanup = async () => {
    if(!window.confirm('This will permanently delete temporary files older than 30 days. Proceed?')) return;
    setIsCleaning(true);
    const count = await cleanupOldFiles(30);
    setIsCleaning(false);
    refreshStorageStats();
    addNotification('Storage Cleanup', `Removed ${count} old files.`, 'success');
  };

  const handleFileConfirmed = (file) => {
    setUploadedFile(file);
  };

  const calculateFinancials = (p, q, type, s) => {
    const unitPrice = parseFloat(p) || 0;
    const qty = parseFloat(q) || 0;
    const scrap = parseFloat(s) || 0;

    const effectiveQty = type === 'L/S' ? 1 : qty;
    const totalPrice = unitPrice * effectiveQty;

    let netValue = 0;
    if (totalPrice < 0) {
      netValue = scrap - Math.abs(totalPrice);
    } else {
      netValue = totalPrice;
    }

    return { totalPrice, netValue };
  };

  const { totalPrice: currentTotal, netValue: currentNetValue } = calculateFinancials(price, quantity, unitType, scrapValue);
  const isNegativePrice = (parseFloat(price) || 0) < 0;

  const { totalPrice: editTotal, netValue: editNetValue } = calculateFinancials(editForm.price, editForm.quantity, editForm.unitType, editForm.scrapValue);
  const isEditNegativePrice = (parseFloat(editForm.price) || 0) < 0;

  const determineVersionInfo = (currentList) => {
    const count = currentList.length;
    if (count === 0) {
      return ['Original', 'Original'];
    }
    const revisionNumber = count;
    const versionLabel = `R${revisionNumber}`;
    const typeLabel = revisionNumber === 1 ? 'Revised' : `Revised ${revisionNumber - 1}`;
    return [versionLabel, typeLabel];
  };

  // في handleAddQuotation - استبدل كل الكود القديم بهذا:

const handleAddQuotation = async () => {
  // Validation
  if (!quotationNumber.trim()) {
    alert('Quotation Number is required');
    return;
  }
  if (!description.trim()) {
    alert('Description is required');
    return;
  }
  if (!price || isNaN(parseFloat(price))) {
    alert('Valid price is required');
    return;
  }
  
  const currentPrice = parseFloat(price);
  const isNegativePrice = currentPrice < 0;
  
  if (isNegativePrice && (!scrapValue || parseFloat(scrapValue) <= 0)) {
    alert('Scrap Value is required when price is negative.');
    return;
  }

  setUploading(true);
  
  try {
    let fileId = null;
    let fileName = null;
    let fileSize = 0;
    let fileUrl = null;

    // ✅ رفع الملف إلى السيرفر
    if (uploadedFile) {
      const uploadResult = await quotationsAPI.uploadFile(uploadedFile);
      
      if (!uploadResult.success) {
        throw new Error(uploadResult.error || 'File upload failed');
      }

      fileId = uploadResult.fileId;
      fileName = uploadResult.fileName;
      fileSize = uploadResult.fileSize;
      fileUrl = uploadResult.fileUrl;
    }

    // Determine version
    const [versionLabel, typeLabel] = determineVersionInfo(quotations);
    const { totalPrice, netValue } = calculateFinancials(price, quantity, unitType, scrapValue);

    // ✅ إرسال البيانات مع file_url
    const response = await quotationsAPI.create({
      projectId: id,
      quotationNumber: quotationNumber.trim(),
      description,
      unitType,
      price: parseFloat(price),
      quantity: unitType === 'Unit Rate' ? parseFloat(quantity) : 1,
      scrapValue: isNegativePrice ? parseFloat(scrapValue) : 0,
      total: totalPrice,
      netValue: netValue,
      version: versionLabel,
      versionType: typeLabel,
      fileId,
      fileName,
      fileSize,
      fileUrl // ✅ NEW
    });

    if (response.success) {
      setQuotationNumber('');
      setDescription('');
      setPrice('');
      setScrapValue('');
      setQuantity('1');
      setUnitType('L/S');
      setUploadedFile(null);
      
      addNotification('Quotation Added', 'Record saved successfully.', 'success');
      fetchData();
    }
  } catch (error) {
    console.error(error);
    addNotification('Upload Failed', error.message || 'Could not save file', 'error');
  } finally {
    setUploading(false);
  }
};


  const handleEditClick = (quotation) => {
    setEditingQuotation(quotation);
    setEditForm({
      quotationNumber: quotation.quotationNumber,
      version: quotation.version || '',
      description: quotation.description,
      unitType: quotation.unitType || 'L/S',
      quantity: quotation.quantity || 1,
      price: quotation.price,
      scrapValue: quotation.scrapValue || '',
      keepFile: !!quotation.fileId || !!quotation.pdfFile
    });
    setEditFile(null);
    setEditDialogOpen(true);
  };

 const handleUpdateQuotation = async () => {
  // Validation
  if (!editForm.quotationNumber.trim()) {
    alert('Quotation Number is required');
    return;
  }
  if (!editForm.description.trim()) {
    alert('Description is required');
    return;
  }
  if (!editForm.price || isNaN(parseFloat(editForm.price))) {
    alert('Valid price is required');
    return;
  }
  
  const currentEditPrice = parseFloat(editForm.price);
  const isNowNegative = currentEditPrice < 0;
  
  if (isNowNegative && (!editForm.scrapValue || parseFloat(editForm.scrapValue) <= 0)) {
    alert('Scrap Value is required when price is negative.');
    return;
  }

  setIsSavingEdit(true);
  
  try {
    const { totalPrice, netValue } = calculateFinancials(
      editForm.price, 
      editForm.quantity, 
      editForm.unitType, 
      editForm.scrapValue
    );

    let finalFileId = editingQuotation.fileId;
    let finalFileName = editingQuotation.pdfFileName;
    let finalFileSize = editingQuotation.fileSize;
    let finalFileUrl = editingQuotation.fileUrl;

    // ✅ إذا في ملف جديد - ارفعه واحذف القديم
    if (editFile) {
      // Upload new file
      const uploadResult = await quotationsAPI.uploadFile(editFile);
      
      if (!uploadResult.success) {
        throw new Error(uploadResult.error || 'File upload failed');
      }

      // Delete old file if exists
      if (editingQuotation.pdfFileName) {
        try {
          await quotationsAPI.deleteFile(editingQuotation.pdfFileName);
        } catch (e) {
          console.warn('Old file deletion failed:', e);
        }
      }

      finalFileId = uploadResult.fileId;
      finalFileName = uploadResult.fileName;
      finalFileSize = uploadResult.fileSize;
      finalFileUrl = uploadResult.fileUrl;
    } 
    // ✅ إذا المستخدم حذف الملف الحالي
    else if (!editForm.keepFile && editingQuotation.pdfFileName) {
      try {
        await quotationsAPI.deleteFile(editingQuotation.pdfFileName);
      } catch (e) {
        console.warn('File deletion failed:', e);
      }
      
      finalFileId = null;
      finalFileName = null;
      finalFileSize = 0;
      finalFileUrl = null;
    }

    // ✅ Update quotation
    await quotationsAPI.update(editingQuotation.id, {
      quotationNumber: editForm.quotationNumber,
      version: editForm.version,
      description: editForm.description,
      unitType: editForm.unitType,
      quantity: editForm.unitType === 'Unit Rate' ? parseFloat(editForm.quantity) : 1,
      price: parseFloat(editForm.price),
      scrapValue: isNowNegative ? parseFloat(editForm.scrapValue) : 0,
      total: totalPrice,
      netValue: netValue,
      fileId: finalFileId,
      fileName: finalFileName,
      fileSize: finalFileSize,
      fileUrl: finalFileUrl // ✅ NEW
    });

    setEditDialogOpen(false);
    setEditingQuotation(null);
    
    addNotification('Quotation Updated', 'Changes saved successfully.', 'success');
    fetchData();
  } catch (error) {
    console.error(error);
    addNotification('Update Failed', error.message || 'Could not save changes', 'error');
  } finally {
    setIsSavingEdit(false);
  }
};

  const handleViewFile = async (quotation) => {
  // ✅ إذا في fileUrl، افتحه مباشرة
  if (quotation.fileUrl) {
    window.open(quotation.fileUrl, '_blank');
    return;
  }

  // Fallback: IndexedDB (للملفات القديمة)
  if (quotation.fileId) {
    try {
      const fileRecord = await getFileFromIDB(quotation.fileId);
      if (fileRecord && fileRecord.file) {
        const objUrl = URL.createObjectURL(fileRecord.file);
        openViewer(objUrl, quotation.pdfFileName || 'Document.pdf');
      } else {
        addNotification('File Not Found', 'The file attachment could not be found in storage.', 'error');
      }
    } catch (e) {
      addNotification('Read Error', 'Failed to read file from storage.', 'error');
    }
  } else {
    addNotification('No File', 'No file attached to this quotation.', 'error');
  }
};


 const handleDelete = async (quotationId, fileName) => {
  if (!window.confirm('Are you sure you want to delete this quotation? This cannot be undone.')) {
    return;
  }
  
  try {
    // ✅ Delete will handle file deletion in backend
    await quotationsAPI.delete(quotationId);
    
    addNotification('Quotation Deleted', 'Record removed permanently.', 'warning');
    fetchData();
  } catch (error) {
    console.error(error);
    addNotification('Delete Failed', error.message || 'Could not delete quotation', 'error');
  }
};



  const handleGenerateLink = (quotationId) => {
    const token = btoa(JSON.stringify({ p: id, q: quotationId }));
    const link = `${window.location.origin}/share/quotation/${token}`;
    setCurrentShareLink(link);
    setShareOpen(true);
    setCopied(false);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(currentShareLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleEmailShare = () => {
    const subject = `Quotation for ${project.projectName}`;
    const body = `Please view the quotation here: ${currentShareLink}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  if (!project) return null;

  const latestQuotationId = quotations.length > 0
    ? quotations.reduce((prev, current) => (new Date(prev.createdAt) > new Date(current.createdAt)) ? prev : current).id
    : null;

  const storageUsagePercent = storageStats.quota > 0 ? (storageStats.usage / storageStats.quota) * 100 : 0;

  return (
    <>
      <Helmet>
        <title>Quotations - {project.projectName}</title>
      </Helmet>

      <div className='space-y-6 p-6 max-w-7xl mx-auto animate-in fade-in duration-500'>
        {/* Header */}
        <div className='flex items-center justify-between border-b pb-4'>
          <div className='flex items-center gap-4'>
            <Button
              variant='ghost'
              size='icon'
              onClick={() => navigate(`/projects/${id}`)}
              className='hover:bg-slate-100'
            >
              <ArrowLeft className='h-5 w-5' />
            </Button>
            <div>
              <h1 className='text-3xl font-bold text-navy-steal'>Project Quotations</h1>
              <p className='text-stone-500 mt-1'>{project.projectName}</p>
            </div>
          </div>
        </div>

        {/* Storage Stats Card */}
        <Card className='border-l-4 border-l-blue-500 shadow-sm'>
          <CardHeader className='pb-3'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-2'>
                <Database className='w-5 h-5 text-blue-600' />
                <CardTitle className='text-base'>Local Storage Status</CardTitle>
              </div>
              <Badge variant='outline' className='gap-1'>
                <HardDrive className='w-3 h-3' />
                {storageStats.fileCount} Files Stored
              </Badge>
            </div>
          </CardHeader>
          <CardContent className='space-y-3'>
            <div className='space-y-1'>
              <div className='flex justify-between text-sm'>
                <span className='text-stone-600'>
                  {formatBytes(storageStats.usage)} / {formatBytes(storageStats.quota)}
                </span>
                <span className='font-medium text-stone-700'>
                  {storageUsagePercent.toFixed(1)}%
                </span>
              </div>
              <Progress 
                value={storageUsagePercent} 
                className={cn(
                  'h-2',
                  storageUsagePercent > 80 && 'bg-red-100',
                  storageUsagePercent > 50 && storageUsagePercent <= 80 && 'bg-yellow-100'
                )}
              />
            </div>
            {storageUsagePercent > 70 && (
              <div className='flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md'>
                <AlertCircle className='w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0' />
                <div className='flex-1'>
                  <p className='text-sm text-amber-800 font-medium'>Storage Warning</p>
                  <p className='text-xs text-amber-700 mt-1'>
                    You're using {storageUsagePercent.toFixed(0)}% of available storage. 
                    Consider cleaning up old files.
                  </p>
                </div>
                <Button
                  size='sm'
                  variant='outline'
                  onClick={handleCleanup}
                  disabled={isCleaning}
                  className='text-amber-700 border-amber-300 hover:bg-amber-100 flex-shrink-0'
                >
                  {isCleaning ? 'Cleaning...' : 'Cleanup'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add Quotation Form */}
        <Card className='border-t-4 border-t-safety-orange shadow-md'>
          <CardHeader className='border-b bg-slate-50'>
            <CardTitle className='text-xl flex items-center gap-2 text-navy-steal'>
              <Plus className='w-5 h-5 text-safety-orange' />
              Add New Quotation
            </CardTitle>
          </CardHeader>
          <CardContent className='pt-6'>
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
              {/* Left Column */}
              <div className='space-y-4'>
                <div className='space-y-2'>
                  <Label htmlFor='quotationNumber' className='text-sm font-semibold text-stone-700'>
                    Quotation Number <span className='text-red-500'>*</span>
                  </Label>
                  <Input
                    id='quotationNumber'
                    placeholder='e.g. QT-100'
                    value={quotationNumber}
                    onChange={(e) => setQuotationNumber(e.target.value)}
                    className='bg-slate-50 border-stone-200'
                  />
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='description' className='text-sm font-semibold text-stone-700'>
                    Description <span className='text-red-500'>*</span>
                  </Label>
                  <Input
                    id='description'
                    placeholder='Brief description of work'
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className='bg-slate-50 border-stone-200'
                  />
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='unitType' className='text-sm font-semibold text-stone-700'>
                    Unit Type
                  </Label>
                  <Select value={unitType} onValueChange={setUnitType}>
                    <SelectTrigger className='bg-slate-50 border-stone-200'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='L/S'>L/S (Lump Sum)</SelectItem>
                      <SelectItem value='Unit Rate'>Unit Rate</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className='grid grid-cols-2 gap-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='price' className='text-sm font-semibold text-stone-700'>
                      Price (AED) <span className='text-red-500'>*</span>
                    </Label>
                    <Input
                      id='price'
                      type='number'
                      step='0.01'
                      placeholder='0.00'
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      className='bg-slate-50 border-stone-200'
                    />
                  </div>

                  {unitType === 'Unit Rate' && (
                    <div className='space-y-2'>
                      <Label htmlFor='quantity' className='text-sm font-semibold text-stone-700'>
                        Quantity
                      </Label>
                      <Input
                        id='quantity'
                        type='number'
                        step='0.01'
                        placeholder='1'
                        value={quantity}
                        onChange={(e) => setQuantity(e.target.value)}
                        className='bg-slate-50 border-stone-200'
                      />
                    </div>
                  )}
                </div>

                {isNegativePrice && (
                  <div className='space-y-2'>
                    <Label htmlFor='scrapValue' className='text-sm font-semibold text-stone-700 flex items-center gap-2'>
                      <Coins className='w-4 h-4 text-green-600' />
                      Scrap Value (AED) <span className='text-red-500'>*</span>
                    </Label>
                    <Input
                      id='scrapValue'
                      type='number'
                      step='0.01'
                      placeholder='0.00'
                      value={scrapValue}
                      onChange={(e) => setScrapValue(e.target.value)}
                      className='bg-green-50 border-green-200'
                    />
                    <p className='text-xs text-green-700'>
                      Required for negative pricing. Net value will be calculated as: Scrap Value - |Price|
                    </p>
                  </div>
                )}
              </div>

              {/* Right Column - File Upload + Summary */}
              <div className='space-y-4'>
                <div className='space-y-2'>
                  <Label className='text-sm font-semibold text-stone-700'>Attachment</Label>
                  <FileUpload
                    onFileSelect={handleFileConfirmed}
                    onUploadStatusChange={setUploading}
                    label='Upload Any File'
                    maxSizeMB={250}
                    acceptedTypes={null}
                  />
                  {uploadedFile && (
                    <div className='flex items-center justify-between bg-green-50 border border-green-200 p-3 rounded-lg mt-2'>
                      <div className='flex items-center gap-3'>
                        <Check className='w-5 h-5 text-green-600' />
                        <div>
                          <p className='font-semibold text-green-900 text-sm'>{uploadedFile.name}</p>
                          <p className='text-xs text-green-700'>{formatBytes(uploadedFile.size)} - Ready</p>
                        </div>
                      </div>
                      <Button
                        type='button'
                        variant='ghost'
                        size='sm'
                        onClick={() => setUploadedFile(null)}
                        className='text-red-500 hover:text-red-700 hover:bg-red-50'
                      >
                        Remove
                      </Button>
                    </div>
                  )}
                </div>

                {/* Financial Summary */}
                <Card className='bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200'>
                  <CardHeader className='pb-3'>
                    <CardTitle className='text-sm text-blue-900'>Financial Summary</CardTitle>
                  </CardHeader>
                  <CardContent className='space-y-2'>
                    <div className='flex justify-between text-sm'>
                      <span className='text-blue-700'>Total Price:</span>
                      <span className={cn(
                        'font-bold',
                        currentTotal < 0 ? 'text-red-600' : 'text-blue-900'
                      )}>
                        {formatCurrency(currentTotal)}
                      </span>
                    </div>
                    {isNegativePrice && scrapValue && (
                      <div className='flex justify-between text-sm'>
                        <span className='text-green-700'>Scrap Value:</span>
                        <span className='font-bold text-green-800'>
                          {formatCurrency(parseFloat(scrapValue) || 0)}
                        </span>
                      </div>
                    )}
                    <div className='pt-2 border-t border-blue-300 flex justify-between'>
                      <span className='font-semibold text-blue-900'>Net Value:</span>
                      <span className={cn(
                        'font-bold text-lg',
                        currentNetValue < 0 ? 'text-red-600' : 'text-green-700'
                      )}>
                        {formatCurrency(currentNetValue)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            <div className='flex justify-end gap-3 pt-6 mt-6 border-t'>
              <Button
                type='button'
                variant='outline'
                onClick={() => {
                  setQuotationNumber('');
                  setDescription('');
                  setPrice('');
                  setScrapValue('');
                  setQuantity('1');
                  setUnitType('L/S');
                  setUploadedFile(null);
                }}
              >
                Clear Form
              </Button>
              <Button
                onClick={handleAddQuotation}
                disabled={uploading}
                className='bg-navy-steal hover:bg-charcoal text-white min-w-[180px]'
              >
                {uploading ? 'Saving...' : <><Plus className='w-4 h-4 mr-2' /> Add Quotation</>}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Quotations List */}
        <Card className='shadow-md'>
          <CardHeader className='bg-slate-50 border-b'>
            <div className='flex items-center justify-between'>
              <CardTitle className='text-xl text-navy-steal'>Quotations List</CardTitle>
              <Badge variant='outline' className='bg-white text-stone-500'>
                {quotations.length} Total
              </Badge>
            </div>
          </CardHeader>
          <CardContent className='p-0'>
            {quotations.length === 0 ? (
              <div className='flex flex-col items-center justify-center h-80 text-stone-400'>
                <FileText className='w-16 h-16 opacity-50 mb-4' />
                <p className='text-lg font-medium text-stone-600'>No quotations yet</p>
                <p className='text-sm'>Add your first quotation using the form above</p>
              </div>
            ) : (
              <Table>
                <TableHeader className='bg-white'>
                  <TableRow className='hover:bg-transparent border-b-2 border-stone-100'>
                    <TableHead className='font-bold text-navy-steal uppercase text-xs'>Quotation #</TableHead>
                    <TableHead className='font-bold text-navy-steal uppercase text-xs'>Description</TableHead>
                    <TableHead className='font-bold text-navy-steal uppercase text-xs'>Type</TableHead>
                    <TableHead className='font-bold text-navy-steal uppercase text-xs text-right'>Price</TableHead>
                    <TableHead className='font-bold text-navy-steal uppercase text-xs text-right'>Quantity</TableHead>
                    <TableHead className='font-bold text-navy-steal uppercase text-xs text-right'>Net Value</TableHead>
                    <TableHead className='font-bold text-navy-steal uppercase text-xs'>Version</TableHead>
                    <TableHead className='font-bold text-navy-steal uppercase text-xs'>File</TableHead>
                    <TableHead className='font-bold text-navy-steal uppercase text-xs text-right'>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {quotations.map((quot) => (
                    <TableRow 
                      key={quot.id} 
                      className={cn(
                        'group hover:bg-slate-50/80 transition-colors border-b border-stone-50',
                        quot.id === latestQuotationId && 'bg-blue-50/30'
                      )}
                    >
                      <TableCell className='font-medium text-navy-steal'>
                        {quot.quotationNumber}
                        {quot.id === latestQuotationId && (
                          <Badge variant='secondary' className='ml-2 bg-blue-100 text-blue-700 text-xs'>Latest</Badge>
                        )}
                      </TableCell>
                      <TableCell className='max-w-xs'>
                        <p className='line-clamp-2 text-sm text-stone-700'>{quot.description}</p>
                      </TableCell>
                      <TableCell>
                        <Badge variant='outline' className='bg-slate-50 border-slate-300 text-slate-700'>
                          {quot.unitType}
                        </Badge>
                      </TableCell>
                      <TableCell className='text-right'>
                        <span className={cn(
                          'font-semibold',
                          quot.price < 0 ? 'text-red-600' : 'text-stone-700'
                        )}>
                          {formatCurrency(quot.price)}
                        </span>
                      </TableCell>
                      <TableCell className='text-right text-stone-600'>
                        {quot.quantity}
                      </TableCell>
                      <TableCell className='text-right'>
                        <div className='flex flex-col items-end'>
                          <span className={cn(
                            'font-bold text-base',
                            quot.netValue < 0 ? 'text-red-600' : 'text-green-700'
                          )}>
                            {formatCurrency(quot.netValue)}
                          </span>
                          {quot.scrapValue > 0 && (
                            <span className='text-xs text-green-600 flex items-center gap-1 mt-1'>
                              <Coins className='w-3 h-3' />
                              Scrap: {formatCurrency(quot.scrapValue)}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className='flex flex-col'>
                          <Badge variant='secondary' className='bg-purple-100 text-purple-700 text-xs w-fit'>
                            {quot.version}
                          </Badge>
                          <span className='text-10px text-stone-400 mt-1'>{quot.versionType}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {quot.fileId || quot.pdfFile ? (
                          <div className='flex items-center gap-2'>
                            <FileText className='w-4 h-4 text-blue-500' />
                            <div className='flex flex-col'>
                              <span className='text-xs text-stone-700 font-medium truncate max-w-[120px]'>
                                {quot.pdfFileName || 'Document.pdf'}
                              </span>
                              {quot.fileSize && (
                                <span className='text-10px text-stone-400'>{formatBytes(quot.fileSize)}</span>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className='text-xs text-stone-400'>No file</span>
                        )}
                      </TableCell>
                      <TableCell className='text-right'>
                        <div className='flex justify-end gap-1 opacity-70 group-hover:opacity-100 transition-opacity'>
                          {(quot.fileId || quot.pdfFile) && (
                            <Button
                              size='icon'
                              variant='ghost'
                              onClick={() => handleViewFile(quot)}
                              className='h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50'
                              title='View File'
                            >
                              <Eye className='w-4 h-4' />
                            </Button>
                          )}
                          <Button
                            size='icon'
                            variant='ghost'
                            onClick={() => handleGenerateLink(quot.id)}
                            className='h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50'
                            title='Share Link'
                          >
                            <LinkIcon className='w-4 h-4' />
                          </Button>
                          <Button
                            size='icon'
                            variant='ghost'
                            onClick={() => handleEditClick(quot)}
                            className='h-8 w-8 text-amber-600 hover:text-amber-700 hover:bg-amber-50'
                            title='Edit'
                          >
                            <Edit className='w-4 h-4' />
                          </Button>
                          <Button
                            size='icon'
                            variant='ghost'
                            onClick={() => handleDelete(quot.id, quot.fileId)}
                            className='h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50'
                            title='Delete'
                          >
                            <Trash2 className='w-4 h-4' />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className='max-w-3xl max-h-[90vh] overflow-y-auto'>
            <DialogHeader>
              <DialogTitle className='text-xl flex items-center gap-2 text-navy-steal'>
                <Edit className='w-5 h-5 text-amber-600' />
                Edit Quotation
              </DialogTitle>
              <DialogDescription>
                Modify quotation details. Changes will be saved immediately.
              </DialogDescription>
            </DialogHeader>

            <div className='grid grid-cols-1 lg:grid-cols-2 gap-6 py-4'>
              {/* Left Column */}
              <div className='space-y-4'>
                <div className='space-y-2'>
                  <Label htmlFor='edit-quotationNumber' className='text-sm font-semibold'>
                    Quotation Number <span className='text-red-500'>*</span>
                  </Label>
                  <Input
                    id='edit-quotationNumber'
                    value={editForm.quotationNumber}
                    onChange={(e) => setEditForm({ ...editForm, quotationNumber: e.target.value })}
                    className='bg-slate-50'
                  />
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='edit-version' className='text-sm font-semibold'>
                    Version
                  </Label>
                  <Input
                    id='edit-version'
                    value={editForm.version}
                    onChange={(e) => setEditForm({ ...editForm, version: e.target.value })}
                    className='bg-slate-50'
                    placeholder='e.g. R1, R2'
                  />
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='edit-description' className='text-sm font-semibold'>
                    Description <span className='text-red-500'>*</span>
                  </Label>
                  <Input
                    id='edit-description'
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    className='bg-slate-50'
                  />
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='edit-unitType' className='text-sm font-semibold'>
                    Unit Type
                  </Label>
                  <Select 
                    value={editForm.unitType} 
                    onValueChange={(value) => setEditForm({ ...editForm, unitType: value })}
                  >
                    <SelectTrigger className='bg-slate-50'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='L/S'>L/S (Lump Sum)</SelectItem>
                      <SelectItem value='Unit Rate'>Unit Rate</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className='grid grid-cols-2 gap-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='edit-price' className='text-sm font-semibold'>
                      Price (AED) <span className='text-red-500'>*</span>
                    </Label>
                    <Input
                      id='edit-price'
                      type='number'
                      step='0.01'
                      value={editForm.price}
                      onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                      className='bg-slate-50'
                    />
                  </div>

                  {editForm.unitType === 'Unit Rate' && (
                    <div className='space-y-2'>
                      <Label htmlFor='edit-quantity' className='text-sm font-semibold'>
                        Quantity
                      </Label>
                      <Input
                        id='edit-quantity'
                        type='number'
                        step='0.01'
                        value={editForm.quantity}
                        onChange={(e) => setEditForm({ ...editForm, quantity: e.target.value })}
                        className='bg-slate-50'
                      />
                    </div>
                  )}
                </div>

                {isEditNegativePrice && (
                  <div className='space-y-2'>
                    <Label htmlFor='edit-scrapValue' className='text-sm font-semibold flex items-center gap-2'>
                      <Coins className='w-4 h-4 text-green-600' />
                      Scrap Value (AED) <span className='text-red-500'>*</span>
                    </Label>
                    <Input
                      id='edit-scrapValue'
                      type='number'
                      step='0.01'
                      value={editForm.scrapValue}
                      onChange={(e) => setEditForm({ ...editForm, scrapValue: e.target.value })}
                      className='bg-green-50 border-green-200'
                    />
                  </div>
                )}
              </div>

              {/* Right Column - File Management + Summary */}
              <div className='space-y-4'>
                <div className='space-y-2'>
                  <Label className='text-sm font-semibold'>Attachment</Label>
                  
                  {editForm.keepFile && !editFile && (
                    <div className='flex items-center justify-between bg-blue-50 border border-blue-200 p-3 rounded-lg'>
                      <div className='flex items-center gap-3'>
                        <FileText className='w-5 h-5 text-blue-600' />
                        <div>
                          <p className='font-semibold text-blue-900 text-sm'>
                            {editingQuotation?.pdfFileName || 'Existing File'}
                          </p>
                          {editingQuotation?.fileSize && (
                            <p className='text-xs text-blue-700'>{formatBytes(editingQuotation.fileSize)}</p>
                          )}
                        </div>
                      </div>
                      <Button
                        type='button'
                        variant='ghost'
                        size='sm'
                        onClick={() => setEditForm({ ...editForm, keepFile: false })}
                        className='text-red-500 hover:text-red-700 hover:bg-red-50'
                      >
                        <X className='w-4 h-4' />
                      </Button>
                    </div>
                  )}

                  {!editForm.keepFile && !editFile && (
                    <FileUpload
                      onFileSelect={setEditFile}
                      label='Upload New File'
                      maxSizeMB={250}
                      acceptedTypes={null}
                    />
                  )}

                  {editFile && (
                    <div className='flex items-center justify-between bg-green-50 border border-green-200 p-3 rounded-lg'>
                      <div className='flex items-center gap-3'>
                        <Check className='w-5 h-5 text-green-600' />
                        <div>
                          <p className='font-semibold text-green-900 text-sm'>{editFile.name}</p>
                          <p className='text-xs text-green-700'>{formatBytes(editFile.size)} - New Upload</p>
                        </div>
                      </div>
                      <Button
                        type='button'
                        variant='ghost'
                        size='sm'
                        onClick={() => {
                          setEditFile(null);
                          if (editingQuotation?.fileId) {
                            setEditForm({ ...editForm, keepFile: true });
                          }
                        }}
                        className='text-red-500 hover:text-red-700 hover:bg-red-50'
                      >
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>

                {/* Financial Summary */}
                <Card className='bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200'>
                  <CardHeader className='pb-3'>
                    <CardTitle className='text-sm text-amber-900'>Updated Summary</CardTitle>
                  </CardHeader>
                  <CardContent className='space-y-2'>
                    <div className='flex justify-between text-sm'>
                      <span className='text-amber-700'>Total Price:</span>
                      <span className={cn(
                        'font-bold',
                        editTotal < 0 ? 'text-red-600' : 'text-amber-900'
                      )}>
                        {formatCurrency(editTotal)}
                      </span>
                    </div>
                    {isEditNegativePrice && editForm.scrapValue && (
                      <div className='flex justify-between text-sm'>
                        <span className='text-green-700'>Scrap Value:</span>
                        <span className='font-bold text-green-800'>
                          {formatCurrency(parseFloat(editForm.scrapValue) || 0)}
                        </span>
                      </div>
                    )}
                    <div className='pt-2 border-t border-amber-300 flex justify-between'>
                      <span className='font-semibold text-amber-900'>Net Value:</span>
                      <span className={cn(
                        'font-bold text-lg',
                        editNetValue < 0 ? 'text-red-600' : 'text-green-700'
                      )}>
                        {formatCurrency(editNetValue)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            <DialogFooter className='gap-2'>
              <Button
                type='button'
                variant='outline'
                onClick={() => setEditDialogOpen(false)}
                disabled={isSavingEdit}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateQuotation}
                disabled={isSavingEdit}
                className='bg-amber-600 hover:bg-amber-700 text-white'
              >
                {isSavingEdit ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Share Dialog */}
        <Dialog open={shareOpen} onOpenChange={setShareOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className='flex items-center gap-2 text-navy-steal'>
                <LinkIcon className='w-5 h-5 text-green-600' />
                Share Quotation
              </DialogTitle>
              <DialogDescription>
                Share this quotation link with clients or stakeholders
              </DialogDescription>
            </DialogHeader>

            <div className='space-y-4 py-4'>
              <div className='flex items-center gap-2'>
                <Input
                  value={currentShareLink}
                  readOnly
                  className='flex-1 bg-slate-50 text-sm'
                />
                <Button
                  onClick={handleCopyLink}
                  variant={copied ? 'default' : 'outline'}
                  className={cn(
                    'min-w-[100px]',
                    copied && 'bg-green-600 hover:bg-green-700 text-white'
                  )}
                >
                  {copied ? (
                    <>
                      <Check className='w-4 h-4 mr-2' />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className='w-4 h-4 mr-2' />
                      Copy
                    </>
                  )}
                </Button>
              </div>

              <div className='flex items-center gap-2 text-xs text-stone-500'>
                <AlertCircle className='w-4 h-4' />
                <span>This link allows view-only access to the quotation</span>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant='outline'
                onClick={() => setShareOpen(false)}
              >
                Close
              </Button>
              <Button
                onClick={handleEmailShare}
                className='bg-blue-600 hover:bg-blue-700 text-white'
              >
                <Mail className='w-4 h-4 mr-2' />
                Send via Email
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>
    </>
  );
};

export default ProjectQuotationPage;
